//@ts-ignore
import { createEffect, createSignal, onMount, createMemo, Index } from "solid-js";
import { View, Text, hexColor, Show } from "@lightningtv/solid";
import { useNavigate, useParams } from "@solidjs/router";
import { setGlobalBackground, setPreviewToNotShow } from "../../../state";
import { Row } from "@lightningtv/solid-ui";
import { createInfiniteScroll } from "../../components/pagination";
import { LazyUp } from "@lightningtv/solid/primitives";
import searchLive from "../../api/providers/searchLive";
import LoadingIndicator from "../loading/loading";
import videoPlayer from "../../video";
import Header from "../../components/Header";
import { fontFamily, isRTL, t } from "../../translations/configuration";
import { IconButton } from "../../components/playlistComponents/TopRightHeaders";
import KbPanel from "../../components/KbPanel";
import { getImageUrlAbsolute } from "../../api";

const PLACEHOLDER_IMG = "https://assets.orbixplay.com/no_movie_image_placeholder.png";

const SearchLive = () => {
  const [inputValue, setInputValue] = createSignal("");
  const [isLoading, setIsLoading] = createSignal(true);
  const [hasError, setHasError] = createSignal(false);
  const [focusedItem, setFocusedItem] = createSignal<any>(null);
  const [failedImages, setFailedImages] = createSignal<Set<string>>(new Set());

  let kbPanelRef: { setFocus: () => void } | undefined;
  let settingsRef: any, headerRef: any;

  const params = useParams();
  const navigate = useNavigate();

  const handleImageError = (id: string) => {
    setFailedImages(prev => new Set([...prev, id]));
  };

  const [data, setData] = createSignal(null);

  const providerCategory = createMemo(() =>
    createInfiniteScroll(searchLive(params.filter || "all"))
  );

  createEffect(() => {
    try {
      setIsLoading(true);
      setHasError(false);
      const pages: any = providerCategory().pages();
      if (pages) {
        setData(pages);
        setIsLoading(false);
        setTimeout(() => kbPanelRef?.setFocus(), 100);
      }
    } catch (error) {
      console.error("Error loading live channels:", error);
      setIsLoading(false);
      setHasError(true);
    }
  });

  onMount(() => {
    setGlobalBackground("#0A1C34");
    if (videoPlayer) videoPlayer.destroy();
  });

  createEffect(() => { localStorage.setItem("searchInput", inputValue()); });

  onMount(() => {
    const stored = localStorage.getItem("searchInput");
    if (stored) setInputValue(stored);
  });

  const [displayedItems, setDisplayedItems] = createSignal([]);

  // ── RTL channel row ──────────────────────────────────────────────────────
  const RTL_VISIBLE = 6;
  const RTL_STEP = 190; // item width 174 + gap 16
  const RTL_ITEM_W = 174;
  const RTL_PANEL_W = 1200;

  const [rtlViewStart, setRtlViewStart] = createSignal(0);
  const [rtlFocusIdx, setRtlFocusIdx] = createSignal(0); // 0 = rightmost visible
  const rtlRefs: any[] = Array(RTL_VISIBLE).fill(null);

  createEffect(() => { displayedItems(); setRtlViewStart(0); setRtlFocusIdx(0); });

  const rtlWindowItems = () => displayedItems().slice(rtlViewStart(), rtlViewStart() + RTL_VISIBLE);
  const rtlX = (rel: number) => RTL_PANEL_W - RTL_ITEM_W - rel * RTL_STEP; // 1026, 836, 646…

  const rtlMoveLeft = () => {
    if (isLoading() || hasError()) return;
    if (rtlFocusIdx() < RTL_VISIBLE - 1) {
      const nf = rtlFocusIdx() + 1;
      setRtlFocusIdx(nf);
      setTimeout(() => rtlRefs[nf]?.setFocus(), 0);
    } else if (rtlViewStart() + RTL_VISIBLE < displayedItems().length) {
      setRtlViewStart(s => s + 1);
      setTimeout(() => rtlRefs[RTL_VISIBLE - 1]?.setFocus(), 0);
    }
  };

  const rtlMoveRight = () => {
    if (isLoading() || hasError()) return;
    if (rtlFocusIdx() > 0) {
      const nf = rtlFocusIdx() - 1;
      setRtlFocusIdx(nf);
      setTimeout(() => rtlRefs[nf]?.setFocus(), 0);
    } else if (rtlViewStart() > 0) {
      setRtlViewStart(s => s - 1);
      setTimeout(() => rtlRefs[0]?.setFocus(), 0);
    } else {
      kbPanelRef?.setFocus(); // at CH1 (rightmost), exit to keyboard
    }
  };

  const filterData = () => {
    if (!inputValue()) return providerCategory().pages();
    return providerCategory().pages()
      .filter((item: any) => item.title.toLowerCase().includes(inputValue().toLowerCase()));
  };

  createEffect(() => {
    const filtered: any = filterData();
    setDisplayedItems([]);
    setTimeout(() => setDisplayedItems(filtered), 0);
  });

  let viewRef: any;
  const titleSignal = createSignal("");
  const [resetLazyUp, setResetLazyUp] = createSignal(false);

  createEffect(() => {
    titleSignal[0]();
    setResetLazyUp(false);
    setTimeout(() => setResetLazyUp(true), 0);
  });

  const handleKeypadInteraction = (callback: () => void) => {
    if (!isLoading() && !hasError()) callback();
  };

  return (
    <View color={hexColor("#0d0f14")} width={1920} height={1080}>
      <View
        width={1920}
        height={1080}
        positionAbsolute={0}
        onEscape={() => { if (!isLoading()) navigate(-1); }}
      >
        <Header screen={t("search")} ref={(el) => headerRef = el}
          onDown={() => handleKeypadInteraction(() => kbPanelRef?.setFocus())}
          onLeft={() => isRTL() ? settingsRef?.setFocus() : null}
          onRight={() => isRTL() ? null : settingsRef?.setFocus()} />

        {/* ── Settings button top-right ── */}
        <View x={isRTL() ? 1 : 1700} y={50}>
          <IconButton
            onRef={(el) => { settingsRef = el; }}
            x={100} y={0}
            src="https://i.ibb.co/Jjp8bmn4/setting.png"
            onEnter={() => handleKeypadInteraction(() => navigate("/userInfo"))}
            onLeft={() => isRTL() ? null : headerRef?.setFocus()}
            onRight={() => isRTL() ? headerRef?.setFocus() : null}
            onDown={() => handleKeypadInteraction(() => kbPanelRef?.setFocus())}
          />
        </View>

        {/* ── SEARCH PANEL (left in LTR, right in RTL) ── */}
        <View
          x={isRTL() ? 1350 : 70} y={200}
          width={500} height={760}
          borderRadius={30}
          color={hexColor("#12151c")}
        >
          {/* Panel title */}
          <Text
            x={30} y={60}
            style={{ fontSize: 30, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}
          >
            {t("search")}
          </Text>

          {/* Close X */}
          <View
            x={440} y={60}
            width={40} height={40}
            borderRadius={10}
            color={hexColor("#202329")}
            onEnter={() => navigate(-1)}
          >
            <Text x={13} y={8} style={{ fontSize: 25, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold" }}>
              x
            </Text>
          </View>

          {/* Search input box */}
          <View
            x={30} y={120}
            width={450} height={70}
            borderRadius={10}
            color={hexColor("#202329")}
            style={{ border: { width: 1, color: hexColor("#2d3748") } }}
          >
            <View x={20} y={23} width={24} height={24} src="https://i.ibb.co/nNhBcw8M/search.png" color={hexColor("#4b5563")} />
            <Text
              x={60} y={27}
              style={{ fontSize: 24, color: hexColor(inputValue() ? "#f0f6fc" : "#ffffff") }}
            >
              {inputValue() || t("searchForChannels") || "Search for channels ..."}
            </Text>
          </View>

          <View x={30} y={200}>
            <KbPanel
              kbRef={(r) => { kbPanelRef = r; }}
              value={inputValue}
              fieldLabel={() => t("search")}
              hidePreview={true}
              onChar={(c) => setInputValue(v => v + c)}
              onBackspace={() => setInputValue(v => v.slice(0, -1))}
              onDone={() => handleKeypadInteraction(() => isRTL() ? rtlRefs[0]?.setFocus() : viewRef?.setFocus())}
            />
          </View>
        </View>

        {/* ── CHANNELS PANEL (right in LTR, left in RTL) ── */}
        <View x={isRTL() ? 70 : 650} y={270} width={1200} height={700}>

          {/* Title + count badge */}
          <View x={0} y={0} style={{ flexDirection: "row" }} gap={16}>
            <Text style={{ fontSize: 36, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("channels")}
            </Text>
            <Show when={data() && displayedItems().length > 0}>
              <View
                x={160} y={6}
                width={145} height={28}
                borderRadius={8}
                color={hexColor("#12151c")}
              >
                <Text x={10} y={6} style={{ fontSize: 18, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold" }}>
                  {String(displayedItems().length)} CHANNELS
                </Text>
              </View>
            </Show>
          </View>

          {/* Loading state */}
          <Show when={isLoading()}>
            <View x={500} y={200}>
              <LoadingIndicator isCategoriesIndicator={false} />
            </View>
          </Show>

          {/* Channel cards row */}
          <Show when={!isLoading() && !hasError()}>

            {/* ── LTR: lazy scrolling row ── */}
            <Show when={!isRTL()}>
              <View y={70} clipping>
                <Show when={resetLazyUp()}>
                  {/* @ts-ignore */}
                  <LazyUp
                    component={Row}
                    ref={viewRef}
                    scroll="always"
                    direction={"row" as any}
                    upCount={8}
                    each={displayedItems()}
                    gap={16}
                    onLeft={() => handleKeypadInteraction(() => kbPanelRef?.setFocus())}
                  >
                    {(item: any) => {
                      const imgSrc = failedImages().has(String(item().stream_id))
                        ? PLACEHOLDER_IMG
                        : item().image_stream
                          ? getImageUrlAbsolute(item().image_stream)
                          : PLACEHOLDER_IMG;
                      return (
                        <View
                          width={174} height={174}
                          borderRadius={12}
                          style={{
                            border: { width: 0, color: hexColor("#2563eb") },
                            focus: { border: { width: 3, color: hexColor("#2563eb") } },
                          }}
                          onUp={() => settingsRef?.setFocus()}
                          onFocus={() => setFocusedItem(item())}
                          onEnter={() => handleKeypadInteraction(() => {
                            setPreviewToNotShow(false);
                            navigate(`/livetv?num=${item().stream_id}&isTrue=true`);
                          })}
                        >
                          <View x={2} y={2} width={170} height={170} borderRadius={12} color={hexColor("#1a1d24")}>
                            <View x={16} y={16} width={130} height={130} src={imgSrc} onError={() => handleImageError(String(item().stream_id))} />
                          </View>
                          <Text x={8} y={212} style={{ fontSize: 18, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
                            {item().title?.length > 16 ? `${item().title.slice(0, 16)}...` : item().title}
                          </Text>
                        </View>
                      );
                    }}
                  </LazyUp>
                </Show>
              </View>
            </Show>

            {/* ── RTL: custom right-to-left windowed row ── */}
            <Show when={isRTL()}>
              <View y={70} width={1200} height={250} clipping>
                <Index each={rtlWindowItems() as any[]}>
                  {(item: any, relIdx) => {
                    const imgSrc = failedImages().has(String(item().stream_id))
                      ? PLACEHOLDER_IMG
                      : item().image_stream
                        ? getImageUrlAbsolute(item().image_stream)
                        : PLACEHOLDER_IMG;
                    return (
                      <View
                        ref={(el: any) => { rtlRefs[relIdx] = el; }}
                        x={rtlX(relIdx)} y={0}
                        width={174} height={174}
                        borderRadius={12}
                        style={{
                          border: { width: 0, color: hexColor("#2563eb") },
                          focus: { border: { width: 3, color: hexColor("#2563eb") } },
                        }}
                        onLeft={rtlMoveLeft}
                        onRight={rtlMoveRight}
                        onUp={() => settingsRef?.setFocus()}
                        onFocus={() => { setFocusedItem(item()); setRtlFocusIdx(relIdx); }}
                        onEnter={() => handleKeypadInteraction(() => {
                          setPreviewToNotShow(false);
                          navigate(`/livetv?num=${item().stream_id}&isTrue=true`);
                        })}
                      >
                        <View x={2} y={2} width={170} height={170} borderRadius={12} color={hexColor("#1a1d24")}>
                          <View x={16} y={16} width={130} height={130} src={imgSrc} onError={() => handleImageError(String(item().stream_id))} />
                        </View>
                        <Text x={8} y={212} style={{ fontSize: 18, color: hexColor("#d1d5db"), fontFamily: fontFamily() }}>
                          {item().title?.length > 16 ? `${item().title.slice(0, 16)}...` : item().title}
                        </Text>
                      </View>
                    );
                  }}
                </Index>
              </View>
            </Show>

            <Show when={displayedItems().length === 0}>
              <Text x={200} y={170} fontSize={30} style={{ color: hexColor("#7d7e82"), fontFamily: fontFamily() }}>
                {t("noChannelsFound")}
              </Text>
            </Show>

            {/* ── Focused channel detail card ── */}
            <Show when={focusedItem()}>
              <View
                x={0} y={350}
                width={1200} height={160}
                borderRadius={16}
                color={hexColor("#15171b")}
              >
                {/* Left blue accent bar */}
                <View
                  x={0} y={0}
                  width={4} height={160}
                  borderRadius={2}
                  color={hexColor("#2563eb")}
                />

                <View x={28} y={18}>
                  {/* Channel name */}
                  <Text style={{ fontSize: 28, color: hexColor("#f0f6fc"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
                    {focusedItem()?.name || focusedItem()?.title}
                  </Text>

                  {/* Meta row: category • language • EPG badge */}
                  <View x={0} y={44} style={{ flexDirection: "row" }} gap={14}>

                    <Show when={focusedItem()?.category_name || focusedItem()?.category}>
                      <Text style={{ fontSize: 21, color: hexColor("#9ca3af") }}>
                        {focusedItem()?.category_name || focusedItem()?.category}
                      </Text>
                      <Text style={{ fontSize: 21, color: hexColor("#374151") }}>•</Text>
                    </Show>

                    <Show when={focusedItem()?.language}>
                      <Text style={{ fontSize: 21, color: hexColor("#9ca3af") }}>
                        {focusedItem()?.language}
                      </Text>
                      <Text style={{ fontSize: 21, color: hexColor("#374151") }}>•</Text>
                    </Show>

                    {/* EPG / live badge */}
                    <View
                      width={62} height={26}
                      borderRadius={6}
                      color={hexColor("#14532d")}
                    >
                      <Text x={10} y={4} style={{ fontSize: 16, color: hexColor("#4ade80"), fontWeight: isRTL() ? "normal" : "bold" }}>
                        ● {t("live")}
                      </Text>
                    </View>

                    <Show when={focusedItem()?.is_adult === "1" || focusedItem()?.is_adult === 1}>
                      <View
                        width={42} height={26}
                        borderRadius={6}
                        color={hexColor("#7f1d1d")}
                      >
                        <Text x={8} y={4} style={{ fontSize: 16, color: hexColor("#fca5a5"), fontWeight: isRTL() ? "normal" : "bold" }}>
                          18+
                        </Text>
                      </View>
                    </Show>
                  </View>

                  {/* EPG now playing (if available) */}
                  <Show when={focusedItem()?.epg_channel_id}>
                    <Text
                      x={0} y={82}
                      style={{ fontSize: 18, color: hexColor("#6b7280"), fontFamily: fontFamily() }}
                    >
                      EPG: {focusedItem()?.epg_channel_id}
                    </Text>
                  </Show>
                </View>

                {/* Press OK hint — bottom right */}
                <View x={24} y={116} style={{ flexDirection: "row" }} gap={10}>
                  <View x={0} y={0} width={32} height={32} borderRadius={16} color={hexColor("#2563eb")}>
                    <View x={2} y={2} width={30} height={30} src="https://i.ibb.co/WNPhwwTN/Arrow-Forward.png" />
                  </View>
                  <Text x={40} y={10} style={{ fontSize: 18, color: hexColor("#8b949e") }}>{t("pressOkToPlay")}</Text>
                </View>
              </View>
            </Show>
          </Show>
        </View>

        {/* Loading overlay */}
        <Show when={isLoading()}>
          <View width={1920} height={1080} color={hexColor("#000000")} alpha={0.7} positionAbsolute={0}>
            <View x={960} y={500}>
              <LoadingIndicator isCategoriesIndicator={false} />
            </View>
          </View>
        </Show>

        {/* Error overlay */}
        <Show when={hasError()}>
          <View width={1920} height={1080} color={hexColor("#000000")} alpha={0.8} positionAbsolute={0}>
            <View x={760} y={400}>
              <Text fontSize={42} color={hexColor("#ffffff")} style={{ fontFamily: fontFamily() }}>
                {t("thereIsProblem")}
              </Text>
              <Text x={50} y={60} fontSize={28} color={hexColor("#8b949e")} style={{ fontFamily: fontFamily() }}>
                {t("tryAgainLater")}
              </Text>
            </View>
          </View>
        </Show>

      </View>
    </View>
  );
};

export default SearchLive;